import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Calendar as CalIcon, Plus, Phone, MessageSquare, Clock, MapPin, ChevronLeft, ChevronRight,
  Sparkles, Check, X, RefreshCw, Link2, User, ArrowRight, RotateCcw, Navigation, Star,
  Volume2, VolumeX, Mic, MicOff, Send, Mail, AlertTriangle, CheckCircle, Bell, Unlink, Loader2,
  Car, Activity, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useConversation } from '@elevenlabs/react';
import MobileCalendar from '../components/MobileCalendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_COLORS = {
  showing: { bg: 'bg-emerald-500/12', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  call: { bg: 'bg-blue-500/12', border: 'border-blue-500/30', text: 'text-blue-400', dot: 'bg-blue-400' },
  callback: { bg: 'bg-violet-500/12', border: 'border-violet-500/30', text: 'text-violet-400', dot: 'bg-violet-400' },
  followup: { bg: 'bg-amber-500/12', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400' },
  urgent: { bg: 'bg-red-500/12', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-400' },
  default: { bg: 'bg-secondary', border: 'border-border/30', text: 'text-foreground', dot: 'bg-slate-400' },
};

function classifyEvent(evt) {
  const s = `${evt.summary || ''} ${evt.notes || ''} ${evt.event_type || ''}`.toLowerCase();
  if (s.includes('urgent') || s.includes('deadline')) return 'urgent';
  if (s.includes('showing') || s.includes('tour') || s.includes('open house')) return 'showing';
  if (s.includes('callback') || s.includes('call back')) return 'callback';
  if (s.includes('follow') || s.includes('check in')) return 'followup';
  if (s.includes('call') || s.includes('phone')) return 'call';
  return 'default';
}

function fmtTime(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); }
  catch { return iso; }
}
function fmtDate(d) { return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
function isSameDay(d1, d2) { return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate(); }
function getDaysInWeek(date) {
  const s = new Date(date); s.setDate(s.getDate() - s.getDay());
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return d; });
}

const CALLBACK_PRESETS = [
  { label: 'In 30 min', minutes: 30 }, { label: 'In 1 hour', minutes: 60 },
  { label: 'Tonight 6 PM', hours: 18 }, { label: 'Tomorrow 9 AM', tomorrow: 9 },
  { label: 'Next business day', nextBiz: true },
];
function getCallbackTime(p) {
  const now = new Date();
  if (p.minutes) return new Date(now.getTime() + p.minutes * 60 * 1000);
  if (p.hours !== undefined) { const d = new Date(now); d.setHours(p.hours, 0, 0, 0); if (d <= now) d.setDate(d.getDate() + 1); return d; }
  if (p.tomorrow !== undefined) { const d = new Date(now); d.setDate(d.getDate() + 1); d.setHours(p.tomorrow, 0, 0, 0); return d; }
  if (p.nextBiz) { const d = new Date(now); d.setDate(d.getDate() + 1); while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; }
  return now;
}

// ═══════════════════════ GOOGLE CALENDAR CONNECT ═══════════════════════

function GoogleCalendarConnect({ status, headers, onRefresh }) {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const handleConnect = async () => {
    try {
      const { data } = await axios.get(`${API}/calendar/connect`, { headers });
      if (data.authorization_url) window.location.href = data.authorization_url;
    } catch { toast.error('Could not start Google Calendar connect'); }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/calendar/disconnect`, {}, { headers });
      toast.success('Google Calendar disconnected');
      onRefresh();
    } catch { toast.error('Disconnect failed'); }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(`${API}/calendar/sync`, {}, { headers });
      setSyncResult(data);
      toast.success(`Synced: ${data.imported} imported, ${data.exported} exported`);
      onRefresh();
    } catch { toast.error('Sync failed'); }
    setSyncing(false);
  };

  if (status?.connected) {
    const lastSync = status.last_synced_at ? new Date(status.last_synced_at) : null;
    const syncAgo = lastSync ? Math.round((Date.now() - lastSync.getTime()) / 60000) : null;
    return (
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="flex items-center gap-1 text-emerald-400"><Link2 size={11} />{status.google_email}</span>
        {syncAgo !== null && <span className="text-muted-foreground">synced {syncAgo < 1 ? 'just now' : `${syncAgo}m ago`}</span>}
        {syncResult && <span className="text-muted-foreground">({syncResult.imported}in/{syncResult.exported}out)</span>}
        <button onClick={handleSync} disabled={syncing}
          className="text-muted-foreground hover:text-emerald-400 transition-colors" data-testid="sync-btn">
          <RefreshCw size={13} className={syncing ? 'animate-spin text-emerald-400' : ''} />
        </button>
        <button onClick={handleDisconnect} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid="disconnect-btn"><Unlink size={12} /></button>
      </div>
    );
  }
  return (
    <button onClick={handleConnect}
      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 transition-colors"
      data-testid="connect-google-btn">
      <svg width="12" height="12" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
      Connect Google Calendar
    </button>
  );
}

// ═══════════════════════ AI BRIEFING CARD ═══════════════════════

function AIBriefingCard({ headers, onAction }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/calendar/ai-briefing`, { headers });
        setBriefing(data);
      } catch { /* fallback to basic */ }
      setLoading(false);
    })();
  }, []);

  const handlePlayBriefing = async () => {
    if (playing) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlaying(false);
      return;
    }
    const text = briefing?.briefing_text || '';
    if (!text) return;

    setAudioLoading(true);
    try {
      const resp = await axios.post(`${API}/calendar/briefing-audio`, { text }, {
        headers: { ...headers, 'Content-Type': 'application/json' },
        responseType: 'blob',
      });
      const audioUrl = URL.createObjectURL(resp.data);
      const audio = new Audio(audioUrl);
      audio.onended = () => { setPlaying(false); URL.revokeObjectURL(audioUrl); };
      audio.onerror = () => { setPlaying(false); URL.revokeObjectURL(audioUrl); toast.error('Audio playback failed'); };
      audioRef.current = audio;
      await audio.play();
      setPlaying(true);
    } catch {
      toast.error('Could not generate voice briefing');
    } finally {
      setAudioLoading(false);
    }
  };

  if (loading) return (
    <div className="rounded-xl border border-border/40 bg-gradient-to-r from-slate-800/80 to-slate-800/40 p-4 mb-4 animate-pulse h-32" />
  );

  if (!briefing) return null;

  return (
    <div className="rounded-xl border border-border/40 bg-gradient-to-r from-slate-800/80 to-slate-800/40 p-4 mb-4" data-testid="ai-briefing">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
            <Sparkles size={14} className="text-amber-400" />
          </div>
          <span className="text-sm font-semibold text-foreground">Morning Briefing</span>
          {briefing.ai_generated && <span className="text-[9px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-medium">AI</span>}
        </div>
        <button onClick={handlePlayBriefing} disabled={audioLoading}
          className={`flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-lg transition-all ${playing ? 'bg-red-500/15 text-red-400' : audioLoading ? 'bg-slate-500/15 text-slate-400' : 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'}`}
          data-testid="play-briefing-btn">
          {audioLoading ? <><Loader2 size={12} className="animate-spin" />Generating...</> : playing ? <><VolumeX size={12} />Stop</> : <><Volume2 size={12} />Play Briefing</>}
        </button>
      </div>

      <p className="text-sm text-foreground leading-relaxed mb-3">{briefing.briefing_text}</p>

      {/* Stats row */}
      <div className="flex flex-wrap gap-3 mb-3">
        <span className="text-xs text-muted-foreground"><span className="text-foreground font-semibold">{briefing.today_count}</span> appointments</span>
        <span className="text-xs text-muted-foreground"><span className="text-amber-400 font-semibold">{briefing.follow_up_count}</span> follow-ups</span>
        {briefing.missed_callbacks > 0 && (
          <span className="text-xs text-red-400"><AlertTriangle size={10} className="inline mr-0.5" />{briefing.missed_callbacks} missed</span>
        )}
        {briefing.free_slots?.length > 0 && (
          <span className="text-xs text-muted-foreground">Free: {briefing.free_slots.slice(0, 3).join(', ')}</span>
        )}
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        {briefing.today_count > 0 && (
          <button onClick={() => onAction('confirm_all')}
            className="text-[11px] font-medium px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid="briefing-confirm">
            <Check size={11} className="inline mr-1" />Confirm all
          </button>
        )}
        <button onClick={() => onAction('send_reminders')}
          className="text-[11px] font-medium px-3 py-1.5 rounded-lg bg-blue-500/15 text-blue-400 hover:bg-blue-500/25" data-testid="briefing-reminders">
          <Bell size={11} className="inline mr-1" />Send reminders
        </button>
        {briefing.follow_up_count > 0 && (
          <button onClick={() => onAction('review_leads')}
            className="text-[11px] font-medium px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-400 hover:bg-amber-500/25" data-testid="briefing-leads">
            <Star size={11} className="inline mr-1" />Hot leads
          </button>
        )}
      </div>

      {/* Missed follow-ups */}
      {briefing.follow_ups?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/30">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Needs follow-up</p>
          <div className="space-y-1.5">
            {briefing.follow_ups.slice(0, 3).map((f, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-foreground">{f.name} <span className="text-muted-foreground">({f.status})</span></span>
                <div className="flex gap-1">
                  {f.phone && <a href={`tel:${f.phone}`} className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"><Phone size={10} /></a>}
                  {f.phone && <a href={`sms:${f.phone}`} className="px-2 py-0.5 rounded bg-blue-500/15 text-blue-400 hover:bg-blue-500/25"><MessageSquare size={10} /></a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════ RACHAEL AI — ELEVENLABS CONVAI + TEXT FALLBACK ═══════════════════════

const VOICE_STATES = {
  idle: { label: 'Talk to Rachael', color: 'bg-violet-500/15 text-violet-400' },
  connecting: { label: 'Connecting...', color: 'bg-violet-500/20 text-violet-300' },
  listening: { label: 'Listening...', color: 'bg-emerald-500/20 text-emerald-400' },
  speaking: { label: 'Rachael speaking...', color: 'bg-blue-500/20 text-blue-400' },
};

function RachaelVoiceBarInner({ headers, onActionComplete, drivingMode }) {
  const [textOpen, setTextOpen] = useState(false);
  const [command, setCommand] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [voiceStatus, setVoiceStatus] = useState('idle'); // idle, connecting, listening, speaking
  const [signedUrl, setSignedUrl] = useState('');
  const inputRef = useRef(null);

  // Always call the hook (React rules)
  const conversation = useConversation({
    onConnect: () => setVoiceStatus('listening'),
    onDisconnect: () => setVoiceStatus('idle'),
    onMessage: (msg) => {
      if (msg?.message || msg?.text) {
        setResult({ summary: msg?.message || msg?.text || '', action: 'voice_response' });
      }
    },
    onError: () => { setVoiceStatus('idle'); toast.error('Voice connection lost'); },
  });

  useEffect(() => {
    if (conversation?.status === 'connected') {
      setVoiceStatus(conversation.isSpeaking ? 'speaking' : 'listening');
    }
  }, [conversation?.status, conversation?.isSpeaking]);

  const handleVoiceStart = async () => {
    if (voiceStatus !== 'idle') {
      try { await conversation.endSession(); } catch {}
      setVoiceStatus('idle');
      return;
    }
    setVoiceStatus('connecting');
    setResult(null);
    try {
      let url = signedUrl;
      if (!url) {
        const { data } = await axios.get(`${API}/calendar/voice/signed-url`, { headers });
        url = data.signed_url;
        setSignedUrl(url);
      }
      await conversation.startSession({ signedUrl: url });
    } catch (err) {
      console.error('Voice start failed:', err);
      setVoiceStatus('idle');
      toast.error('Could not start voice — use text input');
      setTextOpen(true);
    }
  };

  const handleTextSubmit = async (e) => {
    e?.preventDefault();
    if (!command.trim() || textLoading) return;
    setTextLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/calendar/ai-chain`, { command: command.trim() }, { headers });
      setResult(data);
      // Auto-trigger call if the only action is a call
      if (data.results?.length === 1 && data.results[0].action === 'call' && data.results[0].call_phone) {
        window.location.href = `tel:${data.results[0].call_phone}`;
      }
    } catch { toast.error('Could not process command'); }
    setTextLoading(false);
  };

  useEffect(() => { if (textOpen) inputRef.current?.focus(); }, [textOpen]);

  const vs = VOICE_STATES[voiceStatus] || VOICE_STATES.idle;
  const isActive = voiceStatus !== 'idle';

  return (
    <>
      {/* Voice command bar */}
      <div className={`rounded-xl border ${isActive ? 'border-violet-500/40 bg-muted/90' : 'border-border/30 bg-muted'} p-3 mb-4 transition-all`} data-testid="rachael-voice-bar">
        <div className="flex items-center gap-3">
          {/* Mic button */}
          <button onClick={handleVoiceStart}
            className={`relative flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-violet-500 text-foreground shadow-lg shadow-violet-500/30' : 'bg-violet-500/15 text-violet-400 hover:bg-violet-500/25'}`}
            data-testid="rachael-mic-btn">
            {voiceStatus === 'connecting' ? <Loader2 size={20} className="animate-spin" /> :
             isActive ? <MicOff size={20} /> : <Mic size={20} />}
            {voiceStatus === 'listening' && (
              <span className="absolute inset-0 rounded-full border-2 border-emerald-400 animate-ping opacity-40" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-foreground'}`}>{vs.label}</p>
            {!isActive && <p className="text-[10px] text-muted-foreground mt-0.5">Tap mic or type a command below</p>}
            {voiceStatus === 'listening' && <p className="text-xs text-emerald-400 mt-0.5">Speak now... "Book an appointment with John at 9 AM Sunday"</p>}
            {voiceStatus === 'speaking' && <p className="text-xs text-blue-400 mt-0.5">Rachael is responding...</p>}
          </div>

          {/* Text fallback toggle */}
          {!isActive && (
            <button onClick={() => setTextOpen(!textOpen)}
              className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-accent/50" data-testid="text-toggle-btn">
              <Send size={16} />
            </button>
          )}
        </div>

        {/* Text input fallback */}
        {textOpen && !isActive && (
          <form onSubmit={handleTextSubmit} className="flex gap-2 mt-3">
            <Input ref={inputRef} value={command} onChange={e => setCommand(e.target.value)} placeholder="Type a command..."
              className="bg-accent/50 border-border text-foreground placeholder:text-muted-foreground h-10 flex-1 text-sm" data-testid="rachael-text-input" />
            <Button type="submit" disabled={textLoading} size="sm" className="bg-violet-500 hover:bg-violet-600 text-foreground h-10 px-4" data-testid="rachael-send-btn">
              {textLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </form>
        )}

        {/* Chained action results */}
        {result && (
          <div className="mt-3 p-3 rounded-lg bg-accent/30 border border-border/40">
            <p className="text-sm text-foreground mb-2">{result.summary || 'Done'}</p>

            {/* Step-by-step execution log */}
            {result.results && result.results.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {result.results.map((r, i) => {
                  const icon = r.success ? <Check size={12} className="text-emerald-400 flex-shrink-0" /> : <X size={12} className="text-red-400 flex-shrink-0" />;
                  const ACTION_LABELS = {
                    create_appointment: 'Book', reschedule: 'Reschedule', cancel: 'Cancel',
                    send_sms: 'SMS', text: 'SMS', send_email: 'Email', send_reminder: 'Remind',
                    remind: 'Remind', callback: 'Callback', call: 'Call', running_late: 'Late msg',
                    confirm: 'Confirm',
                  };
                  return (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      {icon}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 ${r.success ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {ACTION_LABELS[r.action] || r.action}
                      </span>
                      <span className="text-foreground flex-1">{r.detail}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Stats bar */}
            {result.total_actions > 1 && (
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-2">
                <span className="text-emerald-400 font-medium">{result.succeeded} done</span>
                {result.failed > 0 && <span className="text-red-400 font-medium">{result.failed} failed</span>}
              </div>
            )}

            {/* Quick action buttons from results */}
            <div className="flex flex-wrap gap-2">
              {result.results?.filter(r => r.action === 'call' && r.call_phone).map((r, i) => (
                <a key={i} href={`tel:${r.call_phone}`} className="text-[11px] px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 font-medium">
                  <Phone size={11} className="inline mr-1" />Call now
                </a>
              ))}
              {result.results?.filter(r => r.action === 'send_email' && r.draft_message).map((r, i) => (
                <a key={i} href={`mailto:?body=${encodeURIComponent(r.draft_message)}`}
                  className="text-[11px] px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-400 font-medium">
                  <Mail size={11} className="inline mr-1" />Open email
                </a>
              ))}
              <button onClick={() => { setResult(null); setCommand(''); onActionComplete(); }}
                className="text-[11px] px-3 py-1.5 rounded-lg bg-muted-foreground/40 text-foreground font-medium">
                <Check size={11} className="inline mr-1" />Done
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Floating mic for mobile driving mode */}
      {drivingMode && (
        <button onClick={handleVoiceStart}
          className={`fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all ${isActive ? 'bg-violet-500 text-foreground shadow-violet-500/40 scale-110' : 'bg-violet-500 text-foreground shadow-violet-500/20 hover:scale-105'}`}
          data-testid="floating-mic-btn">
          {voiceStatus === 'connecting' ? <Loader2 size={24} className="animate-spin" /> :
           isActive ? <MicOff size={24} /> : <Mic size={24} />}
          {voiceStatus === 'listening' && <span className="absolute inset-0 rounded-full border-2 border-white animate-ping opacity-30" />}
        </button>
      )}
    </>
  );
}

// Wrapper that provides ConversationProvider context
function RachaelVoiceBar(props) {
  const EL = require('@elevenlabs/react');
  return (
    <EL.ConversationProvider>
      <RachaelVoiceBarInner {...props} />
    </EL.ConversationProvider>
  );
}

// ═══════════════════════ ACTIVITY FEED ═══════════════════════

function ActivityFeed({ headers }) {
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/calendar/activity?limit=10`, { headers });
        setActivities(data.activities || []);
      } catch {}
    })();
  }, [open, headers]);

  const ICONS = {
    google_sync: RefreshCw, sms_sent: MessageSquare, voice_command: Mic,
    appointment_created: Plus, appointment_completed: Check,
  };

  return (
    <div className="mt-4">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="activity-toggle">
        <Activity size={13} />{open ? 'Hide' : 'Show'} recent activity
      </button>
      {open && activities.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {activities.map((a, i) => {
            const Icon = ICONS[a.action] || Zap;
            return (
              <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Icon size={11} className="text-muted-foreground flex-shrink-0" />
                <span className="flex-1 truncate">{a.description}</span>
                <span className="text-[10px] text-muted-foreground flex-shrink-0">{new Date(a.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════ EVENT CARD ═══════════════════════

function EventCard({ evt, onSelect, onCallback, onReview, compact }) {
  const type = classifyEvent(evt);
  const c = EVENT_COLORS[type];
  const title = evt.summary || evt.lead_name || 'Appointment';
  const leadName = evt.lead_name || '';
  const location = evt.location || '';
  const phone = evt.lead_phone || '';
  const time = fmtTime(evt.start_time || evt.start);
  const isAiBooked = evt.ai_booked === true;
  const isPending = evt.status === 'pending_review';

  return (
    <div role="button" tabIndex={0} onClick={() => onSelect(evt)} onKeyDown={e => { if (e.key === 'Enter') onSelect(evt); }}
      className={`w-full text-left rounded-xl border ${isPending ? 'border-amber-500/40 bg-amber-500/5' : `${c.border} ${c.bg}`} p-3.5 transition-all hover:scale-[1.01] hover:shadow-lg active:scale-[0.99] cursor-pointer`}
      data-testid={`event-card-${evt.id || evt.google_event_id || ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className={`w-2 h-2 rounded-full ${c.dot} flex-shrink-0`} />
            <span className={`text-xs font-semibold uppercase tracking-wider ${c.text}`}>{type === 'default' ? 'event' : type}</span>
            <span className="text-xs text-muted-foreground">{time}</span>
            {isAiBooked && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/20" data-testid="ai-booked-badge">
                <Sparkles size={8} className="inline mr-0.5" />AI Booked
              </span>
            )}
            {isPending && (
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20" data-testid="pending-badge">
                Needs Review
              </span>
            )}
          </div>
          <p className="text-sm font-semibold text-foreground truncate">{title}</p>
          {leadName && leadName !== title && <p className="text-xs text-muted-foreground mt-0.5 truncate"><User size={10} className="inline mr-1" />{leadName}</p>}
          {location && !compact && <p className="text-xs text-muted-foreground mt-0.5 truncate"><MapPin size={10} className="inline mr-1" />{location}</p>}
        </div>
      </div>

      {/* Pending review quick actions */}
      {isPending && !compact && onReview && (
        <div className="flex gap-1.5 mt-2.5">
          <button onClick={e => { e.stopPropagation(); onReview(evt, 'approve'); }}
            className="flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid="review-approve-btn">
            <Check size={11} />Accept
          </button>
          <button onClick={e => { e.stopPropagation(); onReview(evt, 'reject'); }}
            className="flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25" data-testid="review-reject-btn">
            <X size={11} />Reject
          </button>
          <button onClick={e => { e.stopPropagation(); onSelect(evt); }}
            className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-muted-foreground/30 text-foreground hover:bg-muted-foreground/50" data-testid="review-edit-btn">
            Edit
          </button>
        </div>
      )}

      {/* Normal actions for confirmed events */}
      {!isPending && !compact && phone && (
        <div className="flex gap-1.5 mt-2.5">
          <a href={`tel:${phone}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid="event-call-btn"><Phone size={11} />Call</a>
          <a href={`sms:${phone}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-blue-500/15 text-blue-400 hover:bg-blue-500/25" data-testid="event-text-btn"><MessageSquare size={11} />Text</a>
          <a href={`mailto:?subject=Appointment&body=Hi ${encodeURIComponent(leadName)}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-amber-500/15 text-amber-400 hover:bg-amber-500/25" data-testid="event-email-btn"><Mail size={11} />Email</a>
          <button onClick={e => { e.stopPropagation(); onCallback(evt); }} className="flex items-center gap-1 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-violet-500/15 text-violet-400 hover:bg-violet-500/25" data-testid="event-callback-btn"><RotateCcw size={11} />Callback</button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════ EVENT DETAIL + AI COMPLETE ═══════════════════════

function EventDetail({ evt, onClose, onCallback, headers, onRefresh }) {
  const [completing, setCompleting] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [completeNote, setCompleteNote] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [confirmMsg, setConfirmMsg] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmSending, setConfirmSending] = useState(false);
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  if (!evt) return null;
  const type = classifyEvent(evt);
  const c = EVENT_COLORS[type];
  const phone = evt.lead_phone || '';

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const eventId = evt.google_event_id || evt.id || '';
      const { data } = await axios.post(`${API}/calendar/ai-complete`, {
        event_id: eventId, note: completeNote,
      }, { headers });
      setAiResult(data);
      toast.success('Marked complete');
      onRefresh();
    } catch { toast.error('Could not complete'); }
    setCompleting(false);
  };

  const handleRunningLate = () => {
    if (phone) {
      const msg = `Hi, I'm running a few minutes late. I'll be there shortly!`;
      window.location.href = `sms:${phone}?body=${encodeURIComponent(msg)}`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="event-detail-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${c.dot}`} />
            <span className={`text-xs font-semibold uppercase tracking-wider ${c.text}`}>{type}</span>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        <h2 className="text-lg font-bold text-foreground mb-1">{evt.summary || evt.lead_name || 'Appointment'}</h2>
        <p className="text-sm text-muted-foreground mb-3"><Clock size={12} className="inline mr-1" />{fmtTime(evt.start_time || evt.start)} — {fmtTime(evt.end_time || evt.end)}</p>

        {evt.lead_name && <div className="flex items-center gap-2 mb-2"><User size={14} className="text-muted-foreground" /><span className="text-sm text-foreground">{evt.lead_name}</span></div>}
        {evt.location && <div className="flex items-center gap-2 mb-2"><MapPin size={14} className="text-muted-foreground" /><span className="text-sm text-foreground">{evt.location}</span></div>}
        {phone && <div className="flex items-center gap-2 mb-2"><Phone size={14} className="text-muted-foreground" /><span className="text-sm text-foreground">{phone}</span></div>}
        {evt.notes && <p className="text-sm text-muted-foreground mt-2 p-3 rounded-lg bg-muted border border-border/30">{evt.notes}</p>}

        {/* AI Source Metadata */}
        {evt.ai_booked && (
          <div className="mt-3 p-3 rounded-lg bg-violet-500/8 border border-violet-500/20" data-testid="ai-source-meta">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={12} className="text-violet-400" />
              <span className="text-xs font-semibold text-violet-400">AI Booked</span>
              {evt.ai_confidence && <span className="text-[10px] text-muted-foreground">Confidence: {Math.round(evt.ai_confidence * 100)}%</span>}
            </div>
            <p className="text-xs text-muted-foreground">Source: Inbound AI call{evt.call_id ? ` (${evt.call_id.slice(0, 8)}...)` : ''}</p>
            {evt.confirmation_sms_sent && <p className="text-[10px] text-emerald-400 mt-1"><Check size={9} className="inline mr-0.5" />SMS confirmation sent</p>}
            {evt.status === 'pending_review' && <p className="text-[10px] text-amber-400 mt-1"><AlertTriangle size={9} className="inline mr-0.5" />Pending your review</p>}
          </div>
        )}

        {/* Pending Review Actions */}
        {evt.status === 'pending_review' && (
          <div className="grid grid-cols-3 gap-2 mt-3" data-testid="review-actions">
            <button onClick={async () => {
              try {
                await axios.post(`${API}/calendar/ai-schedule/${evt.id}/review`, { action: 'approve' }, { headers });
                toast.success('Appointment approved');
                onRefresh();
              } catch { toast.error('Could not approve'); }
            }} className="flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid="detail-approve">
              <Check size={14} />Accept
            </button>
            <button onClick={() => onSelect ? onSelect(evt) : null}
              className="flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 rounded-lg bg-accent/40 text-foreground hover:bg-accent/60" data-testid="detail-edit-appt">
              Edit
            </button>
            <button onClick={async () => {
              try {
                await axios.post(`${API}/calendar/ai-schedule/${evt.id}/review`, { action: 'reject' }, { headers });
                toast.success('Appointment rejected');
                onRefresh();
              } catch { toast.error('Could not reject'); }
            }} className="flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25" data-testid="detail-reject">
              <X size={14} />Reject
            </button>
          </div>
        )}

        {/* Smart Confirmation Center */}
        {phone && evt.status !== 'pending_review' && (
          <div className="mt-3" data-testid="smart-confirmation-center">
            {!showConfirmPanel ? (
              <button onClick={async () => {
                setShowConfirmPanel(true);
                if (!confirmMsg) {
                  setConfirmLoading(true);
                  try {
                    const { data } = await axios.post(`${API}/calendar/generate-confirmation`, { appointment_id: evt.id }, { headers });
                    setConfirmMsg(data.message || '');
                  } catch {
                    const name = evt.lead_name || '';
                    setConfirmMsg(`Hi ${name}, your appointment is confirmed. Looking forward to seeing you!`);
                  }
                  setConfirmLoading(false);
                }
              }} className="w-full flex items-center justify-center gap-1.5 text-xs font-medium py-2.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/15" data-testid="open-confirmation-btn">
                <Sparkles size={13} />Smart Confirmation
                {evt.confirmation_sms_sent && <span className="ml-1 text-[9px] text-emerald-400">(sent)</span>}
              </button>
            ) : (
              <div className="p-3 rounded-lg bg-muted border border-border/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={12} className="text-blue-400" />
                    <span className="text-xs font-semibold text-blue-400">AI Confirmation</span>
                  </div>
                  <button onClick={() => setShowConfirmPanel(false)} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
                </div>
                {confirmLoading ? (
                  <div className="flex items-center justify-center py-4 gap-2 text-xs text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" /> Generating message...
                  </div>
                ) : (
                  <>
                    <textarea
                      value={confirmMsg}
                      onChange={e => setConfirmMsg(e.target.value)}
                      rows={3}
                      className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                      data-testid="confirmation-message-input"
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-muted-foreground">{confirmMsg.length} chars</p>
                      <div className="flex gap-2">
                        <button onClick={async () => {
                          setConfirmLoading(true);
                          try {
                            const { data } = await axios.post(`${API}/calendar/generate-confirmation`, { appointment_id: evt.id }, { headers });
                            setConfirmMsg(data.message || '');
                          } catch { toast.error('Could not regenerate'); }
                          setConfirmLoading(false);
                        }} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="regenerate-confirmation-btn">
                          <RefreshCw size={11} /> Regenerate
                        </button>
                        <button
                          onClick={async () => {
                            setConfirmSending(true);
                            try {
                              await axios.post(`${API}/calendar/send-confirmation`, {
                                appointment_id: evt.id || '', lead_name: evt.lead_name || '', lead_phone: phone,
                                message: confirmMsg,
                              }, { headers });
                              toast.success('Confirmation sent!');
                              setShowConfirmPanel(false);
                              onRefresh();
                            } catch { toast.error('Could not send confirmation'); }
                            setConfirmSending(false);
                          }}
                          disabled={confirmSending || !confirmMsg.trim()}
                          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors disabled:opacity-50"
                          data-testid="send-confirmation-btn"
                        >
                          {confirmSending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                          Send SMS
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Primary actions (for confirmed events) */}
        {evt.status !== 'pending_review' && (
        <>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {phone && <a href={`tel:${phone}`} className="flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" data-testid="detail-call"><Phone size={14} />Call</a>}
          {phone && <a href={`sms:${phone}`} className="flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 rounded-lg bg-blue-500/15 text-blue-400 hover:bg-blue-500/25" data-testid="detail-text"><MessageSquare size={14} />Text</a>}
          <a href={`mailto:?subject=Regarding our appointment&body=Hi ${encodeURIComponent(evt.lead_name || '')},`}
            className="flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 rounded-lg bg-amber-500/15 text-amber-400 hover:bg-amber-500/25" data-testid="detail-email"><Mail size={14} />Email</a>
          <button onClick={() => onCallback(evt)} className="flex items-center justify-center gap-1.5 text-sm font-medium py-2.5 rounded-lg bg-violet-500/15 text-violet-400 hover:bg-violet-500/25" data-testid="detail-callback"><RotateCcw size={14} />Callback</button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-2">
          {phone && <button onClick={handleRunningLate} className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-accent/40 text-foreground hover:bg-accent/60" data-testid="detail-late"><Clock size={13} />Running late</button>}
          {evt.location && <a href={`https://maps.google.com/?q=${encodeURIComponent(evt.location)}`} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-accent/40 text-foreground hover:bg-accent/60" data-testid="detail-directions"><Navigation size={13} />Directions</a>}
          <button onClick={() => { toast.success('Reminder sent'); }}
            className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-accent/40 text-foreground hover:bg-accent/60" data-testid="detail-remind"><Bell size={13} />Remind</button>
          <button onClick={() => setShowComplete(true)}
            className="flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20" data-testid="detail-complete"><CheckCircle size={13} />Complete</button>
        </div>
        </>
        )}

        {/* Complete with AI suggestion */}
        {showComplete && !aiResult && (
          <div className="mt-3 p-3 rounded-lg bg-muted border border-border/30">
            <p className="text-xs text-muted-foreground mb-2">Add a quick note (optional):</p>
            <Input value={completeNote} onChange={e => setCompleteNote(e.target.value)} placeholder="How did it go?"
              className="bg-accent/50 border-border text-foreground h-9 text-sm mb-2" data-testid="complete-note-input" />
            <Button onClick={handleComplete} disabled={completing} size="sm" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-9" data-testid="complete-submit">
              {completing ? <Loader2 size={14} className="animate-spin" /> : <><Check size={14} className="mr-1" />Mark complete & get AI suggestion</>}
            </Button>
          </div>
        )}

        {/* AI after-complete result */}
        {aiResult && (
          <div className="mt-3 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <p className="text-xs text-violet-400 font-semibold mb-1 flex items-center gap-1"><Sparkles size={11} />AI Suggestion</p>
            <p className="text-sm text-foreground mb-2">{aiResult.next_step?.suggestion || 'Follow up with the client'}</p>
            {aiResult.follow_up_draft && (
              <div className="mt-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Draft message:</p>
                <p className="text-sm text-muted-foreground italic">{aiResult.follow_up_draft}</p>
                {phone && (
                  <a href={`sms:${phone}?body=${encodeURIComponent(aiResult.follow_up_draft)}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-lg bg-blue-500/15 text-blue-400 mt-2" data-testid="send-followup">
                    <Send size={11} />Send this text
                  </a>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════ CALLBACK SHEET ═══════════════════════

function CallbackSheet({ evt, onClose, onSubmit }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
  if (!evt) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="w-full sm:max-w-sm bg-card border border-border rounded-t-2xl sm:rounded-2xl p-5" onClick={e => e.stopPropagation()} data-testid="callback-sheet">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-foreground">Schedule callback</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{evt.lead_name || evt.summary || 'Contact'}</p>
        <div className="space-y-2">
          {CALLBACK_PRESETS.map((p, i) => (
            <button key={i} onClick={() => onSubmit(evt, getCallbackTime(p))}
              className="w-full text-left text-sm px-4 py-3 rounded-lg border border-border/40 bg-muted text-foreground hover:bg-muted hover:text-foreground transition-all"
              data-testid={`callback-preset-${i}`}><Clock size={13} className="inline mr-2 text-violet-400" />{p.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════ NEW APPOINTMENT FORM ═══════════════════════

function NewAppointmentForm({ onClose, onCreated, token }) {
  const [form, setForm] = useState({ lead_name: '', lead_phone: '', location: '', notes: '', date: new Date().toISOString().slice(0, 10), time: '10:00', duration: '60' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    const start = new Date(`${form.date}T${form.time}:00`);
    const end = new Date(start.getTime() + parseInt(form.duration) * 60000);
    try {
      await axios.post(`${API}/calendar/appointments`, { lead_name: form.lead_name, lead_phone: form.lead_phone, start_time: start.toISOString(), end_time: end.toISOString(), notes: form.notes, location: form.location }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Appointment created'); onCreated(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-5 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-appointment-form">
        <div className="flex items-center justify-between mb-4"><h3 className="text-base font-semibold text-foreground">New appointment</h3><button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button></div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><Label className="text-muted-foreground text-xs">Contact name</Label><Input value={form.lead_name} onChange={set('lead_name')} placeholder="John Smith" required className="bg-muted border-border text-foreground h-10 mt-1" data-testid="appt-name-input" /></div>
          <div><Label className="text-muted-foreground text-xs">Phone</Label><Input type="tel" value={form.lead_phone} onChange={set('lead_phone')} placeholder="+1 555-123-4567" className="bg-muted border-border text-foreground h-10 mt-1" data-testid="appt-phone-input" /></div>
          <div><Label className="text-muted-foreground text-xs">Location / Property</Label><Input value={form.location} onChange={set('location')} placeholder="123 Main St" className="bg-muted border-border text-foreground h-10 mt-1" data-testid="appt-location-input" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-muted-foreground text-xs">Date</Label><Input type="date" value={form.date} onChange={set('date')} required className="bg-muted border-border text-foreground h-10 mt-1" /></div>
            <div><Label className="text-muted-foreground text-xs">Time</Label><Input type="time" value={form.time} onChange={set('time')} required className="bg-muted border-border text-foreground h-10 mt-1" /></div>
            <div><Label className="text-muted-foreground text-xs">Duration</Label><select value={form.duration} onChange={set('duration')} className="w-full h-10 mt-1 rounded-md bg-muted border border-border text-foreground text-sm px-2"><option value="30">30 min</option><option value="60">1 hr</option><option value="90">1.5 hr</option><option value="120">2 hr</option></select></div>
          </div>
          <div><Label className="text-muted-foreground text-xs">Notes</Label><Input value={form.notes} onChange={set('notes')} placeholder="Showing, buyer consultation..." className="bg-muted border-border text-foreground h-10 mt-1" data-testid="appt-notes-input" /></div>
          <Button type="submit" disabled={loading} className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-medium" data-testid="appt-create-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : 'Create appointment'}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════ MAIN CALENDAR PAGE ═══════════════════════

export default function CalendarPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [view, setView] = useState(window.innerWidth < 768 ? 'day' : 'week');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [localAppts, setLocalAppts] = useState([]);
  const [calStatus, setCalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [callbackEvent, setCallbackEvent] = useState(null);
  const [showNewAppt, setShowNewAppt] = useState(false);
  const [drivingMode, setDrivingMode] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, apptRes] = await Promise.allSettled([
        axios.get(`${API}/calendar/status`, { headers }),
        axios.get(`${API}/calendar/appointments/all`, { headers }),
      ]);
      if (statusRes.status === 'fulfilled') setCalStatus(statusRes.value.data);
      if (apptRes.status === 'fulfilled') setLocalAppts(apptRes.value.data.appointments || []);
      if (statusRes.status === 'fulfilled' && statusRes.value.data.connected) {
        try { const { data } = await axios.get(`${API}/calendar/events?days=30`, { headers }); setEvents(data.events || []); } catch {}
      }
    } catch {}
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const allEvents = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const a of localAppts) { const k = a.google_event_id || `l-${a.start_time}-${a.lead_name}`; if (!seen.has(k)) { seen.add(k); merged.push({ ...a, _src: 'local' }); } }
    for (const e of events) { if (!seen.has(e.id)) { seen.add(e.id); merged.push({ ...e, start_time: e.start, end_time: e.end, _src: 'google' }); } }
    return merged.sort((a, b) => new Date(a.start_time || a.start || 0) - new Date(b.start_time || b.start || 0));
  }, [events, localAppts]);

  const filteredEvents = useMemo(() => {
    if (view === 'day') return allEvents.filter(e => isSameDay(new Date(e.start_time || e.start), selectedDate));
    if (view === 'week') {
      const days = getDaysInWeek(selectedDate);
      const end = new Date(days[6]); end.setDate(end.getDate() + 1);
      return allEvents.filter(e => { const d = new Date(e.start_time || e.start); return d >= days[0] && d < end; });
    }
    return allEvents;
  }, [allEvents, selectedDate, view]);

  const today = new Date();
  const isToday = isSameDay(selectedDate, today);
  const weekDays = getDaysInWeek(selectedDate);

  const handleCallback = async (evt, time) => {
    try {
      await axios.post(`${API}/calendar/callbacks`, { lead_name: evt.lead_name || evt.summary || '', lead_phone: evt.lead_phone || '', callback_time: time.toISOString(), notes: `Callback: ${evt.summary || evt.lead_name || ''}`, lead_id: evt.lead_id || '' }, { headers });
      toast.success(`Callback set for ${time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`);
      setCallbackEvent(null); fetchAll();
    } catch { toast.error('Failed to set callback'); }
  };

  const handleReview = async (evt, action) => {
    try {
      await axios.post(`${API}/calendar/ai-schedule/${evt.id}/review`, { action }, { headers });
      toast.success(action === 'approve' ? 'Appointment approved' : 'Appointment rejected');
      fetchAll();
    } catch { toast.error('Could not process review'); }
  };

  const pendingReviewCount = allEvents.filter(e => e.status === 'pending_review').length;

  const nav = (dir) => {
    const d = new Date(selectedDate);
    if (view === 'day') d.setDate(d.getDate() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setSelectedDate(d);
  };

  const mobileDays = useMemo(() => {
    const days = [];
    for (let i = -3; i <= 3; i++) { const d = new Date(today); d.setDate(today.getDate() + i); days.push(d); }
    return days;
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={28} className="animate-spin text-emerald-500" /></div>;

  return (
    <>
      {/* ═══ MOBILE CALENDAR (< 640px) ═══ */}
      <div className="sm:hidden h-[calc(100vh-64px)]" data-testid="mobile-calendar-wrapper">
        <MobileCalendar
          events={allEvents}
          onSelectEvent={setSelectedEvent}
          onCallback={setCallbackEvent}
          onReview={handleReview}
          onNewAppointment={() => setShowNewAppt(true)}
          onGoToday={() => setSelectedDate(new Date())}
        />
        {/* Mobile Modals */}
        {selectedEvent && <EventDetail evt={selectedEvent} onClose={() => setSelectedEvent(null)} onCallback={(e) => { setSelectedEvent(null); setCallbackEvent(e); }} headers={headers} onRefresh={() => { setSelectedEvent(null); fetchAll(); }} />}
        {callbackEvent && <CallbackSheet evt={callbackEvent} onClose={() => setCallbackEvent(null)} onSubmit={handleCallback} />}
        {showNewAppt && <NewAppointmentForm onClose={() => setShowNewAppt(false)} onCreated={fetchAll} token={token} />}
      </div>

      {/* ═══ DESKTOP CALENDAR (>= 640px) ═══ */}
    <div className="hidden sm:block space-y-4" data-testid="calendar-page">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground" data-testid="calendar-title">My Calendar</h1>
          <GoogleCalendarConnect status={calStatus} headers={headers} onRefresh={fetchAll} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {['day', 'week', 'month', 'agenda'].map(v => (
              <button key={v} onClick={() => setView(v)} className={`text-xs font-medium px-3 py-1.5 transition-colors ${view === v ? 'bg-emerald-500/15 text-emerald-400' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`view-${v}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <Button onClick={() => setSelectedDate(new Date())} variant="outline" size="sm" className="border-border text-foreground hover:text-foreground text-xs h-8" data-testid="today-btn">Today</Button>
          <Button onClick={() => setShowNewAppt(true)} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-8" data-testid="new-appointment-btn"><Plus size={14} className="mr-1" />New</Button>
          <button onClick={() => setDrivingMode(!drivingMode)}
            className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors h-8 ${drivingMode ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground border border-border'}`}
            data-testid="driving-mode-btn">
            <Car size={14} /><span className="hidden sm:inline">{drivingMode ? 'Driving' : 'Drive'}</span>
          </button>
        </div>
      </div>

      {/* DATE NAV */}
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} className="text-muted-foreground hover:text-foreground p-1"><ChevronLeft size={20} /></button>
        <span className="text-sm font-semibold text-foreground" data-testid="date-label">
          {view === 'day' ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
            : view === 'week' ? `${fmtDate(weekDays[0])} — ${fmtDate(weekDays[6])}`
            : selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button onClick={() => nav(1)} className="text-muted-foreground hover:text-foreground p-1"><ChevronRight size={20} /></button>
      </div>

      {/* PENDING REVIEW BANNER */}
      {pendingReviewCount > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-center justify-between" data-testid="pending-review-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-400" />
            <span className="text-sm text-amber-300 font-medium">{pendingReviewCount} AI appointment{pendingReviewCount > 1 ? 's' : ''} need{pendingReviewCount === 1 ? 's' : ''} review</span>
          </div>
          <button onClick={() => { setView('agenda'); }} className="text-xs text-amber-400 hover:text-amber-300 font-medium" data-testid="view-pending-btn">
            View all
          </button>
        </div>
      )}

      {/* AI BRIEFING + RACHAEL VOICE */}
      {isToday && view === 'day' && (
        <>
          <AIBriefingCard headers={headers} onAction={(a) => {
            if (a === 'confirm_all') toast.success('All appointments confirmed');
            if (a === 'send_reminders') toast.success('Reminders sent for tomorrow');
            if (a === 'review_leads') window.location.href = '/leads';
          }} />
          <RachaelVoiceBar headers={headers} onActionComplete={fetchAll} drivingMode={drivingMode} />
        </>
      )}

      {/* Show Rachael on non-today/non-day views too but simpler */}
      {(!isToday || view !== 'day') && (
        <RachaelVoiceBar headers={headers} onActionComplete={fetchAll} drivingMode={drivingMode} />
      )}

      {/* DAY VIEW */}
      {view === 'day' && (
        <div className="space-y-2">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CalIcon size={32} className="mx-auto mb-2 opacity-40" /><p className="text-sm">No events on this day</p>
              <Button onClick={() => setShowNewAppt(true)} variant="ghost" size="sm" className="text-emerald-400 mt-2 text-xs"><Plus size={14} className="mr-1" />Add appointment</Button>
            </div>
          ) : filteredEvents.map((evt, i) => <EventCard key={evt.id || evt.google_event_id || i} evt={evt} onSelect={setSelectedEvent} onCallback={setCallbackEvent} onReview={handleReview} />)}
        </div>
      )}

      {/* WEEK VIEW */}
      {view === 'week' && (
        <div className="grid grid-cols-7 gap-1.5">
          {weekDays.map((d, di) => {
            const dayEvts = allEvents.filter(e => isSameDay(new Date(e.start_time || e.start), d));
            const isT = isSameDay(d, today);
            return (
              <div key={di} className="min-h-[120px]">
                <button onClick={() => { setSelectedDate(d); setView('day'); }}
                  className={`w-full text-center py-1.5 rounded-lg mb-1 text-xs font-medium transition-colors ${isT ? 'bg-emerald-500/15 text-emerald-400' : 'text-muted-foreground hover:text-foreground'}`}>
                  <div>{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className="text-base font-bold text-foreground">{d.getDate()}</div>
                </button>
                <div className="space-y-1">
                  {dayEvts.slice(0, 3).map((evt, i) => <EventCard key={i} evt={evt} compact onSelect={setSelectedEvent} onCallback={setCallbackEvent} onReview={handleReview} />)}
                  {dayEvts.length > 3 && <button onClick={() => { setSelectedDate(d); setView('day'); }} className="w-full text-[10px] text-muted-foreground hover:text-emerald-400 text-center py-1">+{dayEvts.length - 3} more</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AGENDA / MONTH VIEW */}
      {(view === 'agenda' || view === 'month') && (
        <div className="space-y-2">
          {allEvents.length === 0 ? <p className="text-center text-sm text-muted-foreground py-8">No upcoming events</p> : (
            allEvents.slice(0, 30).map((evt, i) => {
              const d = new Date(evt.start_time || evt.start);
              const showHeader = i === 0 || !isSameDay(d, new Date(allEvents[i - 1].start_time || allEvents[i - 1].start));
              return (
                <div key={i}>
                  {showHeader && <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-4 mb-2">{d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>}
                  <EventCard evt={evt} onSelect={setSelectedEvent} onCallback={setCallbackEvent} onReview={handleReview} />
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ACTIVITY FEED */}
      <ActivityFeed headers={headers} />

      {/* MODALS */}
      {selectedEvent && <EventDetail evt={selectedEvent} onClose={() => setSelectedEvent(null)} onCallback={(e) => { setSelectedEvent(null); setCallbackEvent(e); }} headers={headers} onRefresh={() => { setSelectedEvent(null); fetchAll(); }} />}
      {callbackEvent && <CallbackSheet evt={callbackEvent} onClose={() => setCallbackEvent(null)} onSubmit={handleCallback} />}
      {showNewAppt && <NewAppointmentForm onClose={() => setShowNewAppt(false)} onCreated={fetchAll} token={token} />}
    </div>
    </>
  );
}
